import React, { useState } from 'react';
import { ArrowUpRight, Building2, ExternalLink, ShoppingBag, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';

function ClientLogo({ src, name, className = '' }) {
  const [failed, setFailed] = useState(!src);

  if (!src || failed) {
    return (
      <div
        className={`flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm ${className}`}
        aria-hidden
      >
        <span className="text-xs sm:text-sm font-bold tracking-wide text-white/70 text-center px-3 leading-tight">
          {name}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.97] overflow-hidden ${className}`}
    >
      <img
        src={src}
        alt={name}
        className="max-h-full max-w-full object-contain p-2"
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

function ProjectPreview({ project, badgeLabel }) {
  const isEcommerce = project.badge === 'ecommerce';
  const BadgeIcon = isEcommerce ? ShoppingBag : Building2;

  return (
    <div
      className={`relative h-44 sm:h-48 bg-gradient-to-br ${project.previewGradient} border-b border-white/[0.06] overflow-hidden`}
    >
      <div className="absolute inset-0 opacity-40 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.08),transparent_60%)] pointer-events-none" aria-hidden />

      <div className="absolute inset-x-3 top-3 bottom-3 rounded-t-xl border border-white/10 bg-slate-950/75 backdrop-blur-md overflow-hidden shadow-xl group-hover:scale-[1.02] transition-transform duration-500 origin-bottom">
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/[0.06] bg-slate-950/90">
          <div className="w-2 h-2 rounded-full bg-red-400/70" />
          <div className="w-2 h-2 rounded-full bg-amber-400/70" />
          <div className="w-2 h-2 rounded-full bg-emerald-400/70" />
          <div className="ml-2 flex-1 h-1.5 rounded-full bg-white/10 max-w-[55%]" />
        </div>

        <div className="relative p-4 h-[calc(100%-2.25rem)] flex flex-col">
          <div className="flex items-center justify-between gap-2 mb-3">
            <ClientLogo src={project.logo} name={project.name} className="h-8 w-24 sm:h-9 sm:w-28 shrink-0" />
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide shrink-0 ${
                isEcommerce
                  ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200/90'
                  : 'border-sky-500/30 bg-sky-500/10 text-sky-200/90'
              }`}
            >
              <BadgeIcon className="w-2.5 h-2.5" aria-hidden />
              {badgeLabel}
            </span>
          </div>

          <div className="flex-1 space-y-2">
            <div className="h-2.5 w-3/5 rounded-full bg-white/20" />
            <div className="h-2 w-2/5 rounded-full bg-white/10" />
            <div className="grid grid-cols-3 gap-1.5 mt-3">
              <div className="h-14 rounded-lg bg-white/[0.06] border border-white/[0.05]" />
              <div className="h-14 rounded-lg bg-white/[0.04] border border-white/[0.04]" />
              <div className="h-14 rounded-lg bg-white/[0.04] border border-white/[0.04]" />
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-3 right-3 rounded-lg border border-white/10 bg-slate-950/70 backdrop-blur-md px-2 py-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <ExternalLink className="w-3 h-3 text-white/50" aria-hidden />
        <span className="text-[9px] font-medium text-white/45 truncate max-w-[120px]">
          {project.url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
        </span>
      </div>
    </div>
  );
}

export function WebDevRealProjectsSection({ copy }) {
  const badgeLabels = {
    institutional: copy.realProjectBadgeInstitutional,
    ecommerce: copy.realProjectBadgeEcommerce
  };

  return (
    <section id="web-dev-real-projects" className="max-w-6xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0 overflow-x-hidden">
      <div className="text-center mb-10 sm:mb-12">
        <div className="inline-flex max-w-full min-w-0 flex-wrap items-center justify-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-200/90 mb-4">
          <Sparkles className="w-3.5 h-3.5 shrink-0 text-emerald-400" aria-hidden />
          <span className="break-words text-center">{copy.sectionRealProjectsTitle}</span>
        </div>
        <h2 className="text-2xl sm:text-4xl font-black text-white tracking-tight break-words px-1">
          {copy.sectionRealProjectsTitle}
        </h2>
        <p className="text-white/50 mt-3 max-w-2xl mx-auto font-light leading-relaxed break-words">
          {copy.sectionRealProjectsLead}
        </p>
        <div className="w-20 h-1 bg-gradient-to-r from-emerald-500 to-sky-500 mx-auto rounded-full mt-6" />
      </div>

      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3 items-stretch min-w-0">
        {copy.realProjects.map((project) => {
          const badgeLabel = badgeLabels[project.badge] || project.category;
          const isEcommerce = project.badge === 'ecommerce';

          return (
            <article
              key={project.name}
              className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-slate-950/40 backdrop-blur-md hover:border-white/15 hover:shadow-[0_24px_80px_-40px_rgba(52,211,153,0.18)] transition-all duration-300"
            >
              <div
                className={`pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl ${
                  isEcommerce
                    ? 'bg-gradient-to-br from-emerald-500/10 via-transparent to-amber-500/5'
                    : 'bg-gradient-to-br from-sky-500/10 via-transparent to-indigo-500/5'
                }`}
                aria-hidden
              />

              <ProjectPreview project={project} badgeLabel={badgeLabel} />

              <div className="relative flex flex-col flex-1 p-5 sm:p-6">
                <div className="flex items-start gap-3 mb-3">
                  <ClientLogo src={project.logo} name={project.name} className="h-10 w-28 sm:h-11 sm:w-32 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                        isEcommerce
                          ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200/90'
                          : 'border-sky-500/30 bg-sky-500/10 text-sky-200/90'
                      }`}
                    >
                      {badgeLabel}
                    </span>
                  </div>
                </div>

                <h3 className="text-base sm:text-lg font-bold text-white mb-1 break-words group-hover:text-emerald-100/95 transition-colors">
                  {project.name}
                </h3>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-white/40 mb-2">{project.category}</p>
                <p className="text-sm text-white/45 font-light leading-relaxed mb-5 flex-1 break-words">{project.desc}</p>

                <Button
                  asChild
                  variant="outline"
                  className="mt-auto rounded-xl w-full border-white/15 bg-white/[0.04] text-white hover:bg-white/10 hover:border-emerald-500/25 font-semibold h-11 group/btn"
                >
                  <a href={project.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2">
                    {copy.realProjectsVisitCta}
                    <ArrowUpRight className="w-4 h-4 shrink-0 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" aria-hidden />
                  </a>
                </Button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
